import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PORTRAIT_PRIMARY } from "@/lib/assets";

const ease = [0.22, 1, 0.36, 1];

const FACTS = [
  { k: "Experience", v: "8+ years" },
  { k: "Focus", v: "Travel · Edtech · HR" },
  { k: "Based in", v: "New Delhi" },
  { k: "Currently", v: "Sr. UX @ HROne" },
];

export const Hero = ({ profile }) => {
  const portrait = PORTRAIT_PRIMARY;

  return (
    <section data-testid="hero-section" className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-end">
          {/* Headline */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-xs tracking-[0.2em] uppercase text-ash" data-testid="hero-availability">
                Available for new projects
              </span>
            </motion.div>

            <h1 className="font-heading font-light text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl tracking-tight text-cream text-balance">
              {["Hi. I'm a", "UX designer", "based in New Delhi."].map((line, i) => (
                <motion.span
                  key={i}
                  className="block"
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 + i * 0.12, ease }}
                >
                  {line === "UX designer" ? <span className="italic font-extralight text-cream">{line}</span> : line}
                </motion.span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5, ease }}
              className="mt-8 max-w-xl text-ash text-lg leading-relaxed"
            >
              {profile?.subheadline ||
                "8+ years designing product experiences across travel, edtech, and HR & enterprise software — the last 4+ at HROne."}
            </motion.p>

            <motion.dl
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease }}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5 max-w-xl border-t border-white/10 pt-8"
            >
              {FACTS.map((f) => (
                <div key={f.k}>
                  <dt className="text-xs tracking-[0.15em] uppercase text-ash mb-1">{f.k}</dt>
                  <dd className="text-cream text-sm">{f.v}</dd>
                </div>
              ))}
            </motion.dl>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/work"
                data-testid="hero-view-work-button"
                className="group inline-flex items-center gap-2 rounded-full bg-cream text-ink font-medium px-7 py-3.5 hover:bg-white transition-colors"
              >
                View work
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-button"
                className="group inline-flex items-center gap-2 rounded-full border border-white/20 text-cream font-medium px-7 py-3.5 hover:bg-white/10 transition-colors"
              >
                Get in touch
                <ArrowUpRight size={18} className="opacity-60 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
          </div>

          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease }}
            className="lg:col-span-4"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
              <img
                src={portrait}
                alt="Nishant Gathwal"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
